import React, { useState, useEffect } from 'react';
import { format, isValid } from 'date-fns';
import {
  AccountDetails,
  IBroadbandDelivery,
  IBroadbandInstallation,
  Ownership,
  OwnershipFields,
  CheckoutStates,
  GetTechBookingSlotRangeByCode,
  useProductInCart,
  YesNoOptionCheckCodes,
  IUpgradableBoxesStore,
} from '@sky-tv-group/shared';
import { SectionHeader } from '../../header/SectionHeader';
import { arrow, SquareEditIcon } from '../../icon';
import { IArrowDelivery } from '@sky-tv-group/shared/dist/store/arrowDeliveryStore';
import { FormWrapper } from '../../FormWrapper';

interface SectionDetailsProps {
  header: string;
  installationAddress?: string;
  deliveryAddress?: string;
  installationDate: Date | null | undefined;
  installationTime?: string;
  rentOrOwn?: Ownership;
  mainContactAvailability?: string;
  dogs?: string;
  alternateName?: string;
  alternateAreaCode?: string;
  alternatePhoneNumber?: string;
  techNotes?: string;
  areaCode?: string;
  phoneNumber?: string;
  serviceAddress?: string;
  currentAddress?: string;
  fullName?: string;
  ownership?: string;
  onEdit: () => void;
  showSectionHeader?: boolean;
  email?: string;
  deliveryNotes?: string;
}

const SectionDetails = ({
  header,
  installationAddress,
  deliveryAddress,
  installationDate,
  installationTime,
  rentOrOwn,
  mainContactAvailability,
  dogs,
  alternateName,
  alternateAreaCode,
  alternatePhoneNumber,
  techNotes,
  areaCode,
  phoneNumber,
  serviceAddress,
  currentAddress,
  fullName,
  ownership,
  onEdit,
  showSectionHeader = true,
  email,
  deliveryNotes,
}: SectionDetailsProps) => {
  const [slotUnavailability, SetSlotUnavailability] = useState(false);
  useEffect(() => {
    if (installationDate && isValid(installationDate)) {
      const noSlotAvailabilityDate = format(new Date(installationDate), 'd MMM YYY');
      if (noSlotAvailabilityDate === '31 Dec 10000' || noSlotAvailabilityDate === '31 Dec 9999') {
        SetSlotUnavailability(true);
      }
    }
  }, []);

  return (
    <FormWrapper>
      <div className="flex w-full md:mr-4 last:mr-0">
        <div className="w-full flex-1 md:mx-0">
          <div className="flex-col">
            <div className="flex w-full">
              <h4 className="flex-1 sky-h6-black md:sky-h7-black mb-6 md:mb-8">{header}</h4>
              <p className=" text-primary-bright-blue cursor-pointer flex flex-row ml-10" onClick={onEdit}>
                Edit
                <span className="ml-2">
                  <SquareEditIcon />
                </span>
              </p>
            </div>
            <div className="flex flex-col md:gap-6 md:flex-row">
              <div className="md:w-1/2 w-full">
                {(installationAddress || installationAddress || currentAddress) && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Installation address</p>
                    <p className="text-body-xs md:text-link-s">
                      {installationAddress ? installationAddress : serviceAddress ? serviceAddress : currentAddress}
                    </p>
                  </div>
                )}
                {deliveryAddress && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Address</p>
                    <p className="text-body-xs md:text-link-s">{deliveryAddress}</p>
                  </div>
                )}
                {email && (
                  <div className="w-full mb-4 text-primary-mid-night-blue md:hidden">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Email</p>
                    <p className="text-body-xs md:text-link-s">{email}</p>
                  </div>
                )}
                {deliveryNotes && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Delivery instructions</p>
                    <p className="text-body-xs md:text-link-s">{deliveryNotes}</p>
                  </div>
                )}
                {areaCode && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Phone number</p>
                    <p className="text-body-xs md:text-link-s">{`${areaCode} ${phoneNumber}`}</p>
                  </div>
                )}
                {installationDate && isValid(installationDate) && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">{`Preferred ${
                      installationTime ? 'installation' : 'activation'
                    } date`}</p>
                    <p className="text-body-xs md:text-link-s">{`${
                      slotUnavailability
                        ? 'One of our friendly crew will be in touch to schedule an installation for your new Sky device. '
                        : format(new Date(installationDate), 'd MMM YYY')
                    }`}</p>
                  </div>
                )}
                {installationTime && !slotUnavailability && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Preferred installation time</p>
                    <p className="text-body-xs md:text-link-s">{GetTechBookingSlotRangeByCode(installationTime)}</p>
                  </div>
                )}
                {(rentOrOwn || ownership) && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Property details</p>
                    <p className="text-body-xs md:text-link-s">
                      {rentOrOwn === OwnershipFields.Own || ownership === OwnershipFields.Own
                        ? 'You own this property'
                        : 'You rent this property'}
                    </p>
                  </div>
                )}
              </div>
              <div>
                {email && (
                  <div className="w-full mb-4 text-primary-mid-night-blue hidden md:block">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Email</p>
                    <p className="text-body-xs md:text-link-s">{email}</p>
                  </div>
                )}
                {mainContactAvailability === YesNoOptionCheckCodes.NO.value && !slotUnavailability && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Alternative contact</p>
                    <p className="text-body-xs md:text-link-s">
                      {`${alternateName}, ${alternateAreaCode} ${alternatePhoneNumber}`}
                    </p>
                  </div>
                )}
                {dogs && !slotUnavailability && (
                  <div className="w-full mb-4 text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Dogs at home</p>
                    <p className="text-body-xs md:text-link-s">{dogs}</p>
                  </div>
                )}
                {techNotes && (
                  <div className="w-full text-primary-mid-night-blue">
                    <p className="text-body-xs md:text-link-s font-skyBold mb-1">Notes</p>
                    <p className="text-body-xs md:text-link-s">{techNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

interface InstallationDetailsReviewProps {
  edit: (state: string, isDart?: boolean) => void;
  accountDetails: AccountDetails;
  installationDetails?: IUpgradableBoxesStore;
  hasPackage?: boolean;
  hasBroadband?: boolean;
  broadbandDetails?: IBroadbandInstallation;
  broadbandDeliveryDetails?: IBroadbandDelivery;
  arrowDeliveryDetails?: IArrowDelivery;
  isNewFiber?: boolean;
  showSectionHeader?: boolean;
  showMainHeader?: boolean;
  showArrowDeliveryDetails?: boolean;
  showBroadbandDeliveryDetails?: boolean;
  isDartCheckout?: boolean;
  showBroadbandInstallationDetails?: boolean;
  isSkyPODUpgrade?: boolean;
}

const InstallationDetailsReview = ({
  hasPackage = false,
  hasBroadband = false,
  edit,
  accountDetails,
  installationDetails,
  broadbandDetails,
  broadbandDeliveryDetails,
  arrowDeliveryDetails,
  isNewFiber,
  showSectionHeader = true,
  showMainHeader = true,
  showArrowDeliveryDetails,
  showBroadbandDeliveryDetails,
  isDartCheckout,
  showBroadbandInstallationDetails,
  isSkyPODUpgrade,
}: InstallationDetailsReviewProps) => {
  const { requireDeliveryOnly } = useProductInCart();
  return (
    <>
      {showMainHeader && <SectionHeader titleText="Installation Details" />}
      <div className="flex flex-col gap-5 w-full mb-10">
        <div>
          {(hasPackage || (isSkyPODUpgrade && !arrowDeliveryDetails)) && (
            <SectionDetails
              header={'Sky TV Installation Details'}
              installationAddress={accountDetails.propertyDetail.address?.label}
              installationDate={accountDetails.installationDetail.installationDate}
              installationTime={accountDetails.installationDetail.installationTime}
              rentOrOwn={accountDetails.installationDetail.rentOrOwn}
              mainContactAvailability={installationDetails?.mainContactAvailability}
              dogs={installationDetails?.dogs}
              alternateName={installationDetails?.alternateName}
              alternateAreaCode={installationDetails?.alternateAreaCode}
              alternatePhoneNumber={installationDetails?.alternatePhoneNumber}
              techNotes={installationDetails?.techNotes}
              areaCode={installationDetails?.areaCode}
              phoneNumber={installationDetails?.phoneNumber}
              serviceAddress={installationDetails?.serviceAddress?.label}
              currentAddress={installationDetails?.currentAddress}
              fullName={installationDetails?.fullName}
              ownership={installationDetails?.ownership}
              onEdit={() => edit(CheckoutStates.TV, isDartCheckout)}
              showSectionHeader={showSectionHeader}
            />
          )}
        </div>
        {showBroadbandInstallationDetails && (
          <SectionDetails
            header={'Broadband Installation Details'}
            installationAddress={broadbandDeliveryDetails?.installationAddress}
            installationDate={isNewFiber ? broadbandDetails?.installationDate : broadbandDetails?.activationDate}
            installationTime={isNewFiber ? broadbandDetails?.installationTime : undefined}
            rentOrOwn={accountDetails.installationDetail.rentOrOwn}
            onEdit={() => edit(CheckoutStates.BROADBAND)}
            showSectionHeader={showSectionHeader}
          />
        )}
        {((arrowDeliveryDetails && showArrowDeliveryDetails && !isSkyPODUpgrade) ||
          (isSkyPODUpgrade && arrowDeliveryDetails)) && (
          <SectionDetails
            header={'Delivery Details'}
            deliveryAddress={
              arrowDeliveryDetails?.useDifferentDeliveryAddress
                ? arrowDeliveryDetails?.deliveryAddress?.label
                : arrowDeliveryDetails?.installationAddress
            }
            email={arrowDeliveryDetails?.email}
            deliveryNotes={arrowDeliveryDetails?.deliveryNotes?.trim()}
            installationDate={null}
            onEdit={() => edit(CheckoutStates.DELIVERY_DETAILS, isDartCheckout)}
            showSectionHeader={showSectionHeader}
          />
        )}
        {(requireDeliveryOnly || !isSkyPODUpgrade) &&
          !showArrowDeliveryDetails &&
          (accountDetails.propertyDetail.deliveryAddress ||
            arrowDeliveryDetails?.deliveryAddress?.label ||
            arrowDeliveryDetails?.defaultDeliveryAddress) && (
            <SectionDetails
              header={'Delivery Details'}
              deliveryAddress={
                accountDetails.propertyDetail.deliveryAddress?.label ||
                arrowDeliveryDetails?.deliveryAddress?.label ||
                arrowDeliveryDetails?.defaultDeliveryAddress.join(' ')
              }
              email={arrowDeliveryDetails?.email}
              deliveryNotes={arrowDeliveryDetails?.deliveryNotes?.trim()}
              installationDate={null}
              onEdit={() => edit(CheckoutStates.DELIVERY_DETAILS, isDartCheckout)}
              showSectionHeader={showSectionHeader}
            />
          )}
      </div>
    </>
  );
};
export { InstallationDetailsReview };
